import React from 'react';
import { FileSpreadsheet, Phone, BarChart3, FileText, Clock, Users, Shield, Smartphone } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: FileSpreadsheet,
      title: 'Excel Integration',
      description: 'Upload contact lists directly from Excel files (.xlsx/.xls) or paste data manually. Support for CSV files too.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Phone,
      title: 'Automated Calling',
      description: 'Set up automatic calling sequences with customizable delays (1-60 seconds) for systematic lead management.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: BarChart3,
      title: 'Real-time Analytics',
      description: 'Live progress tracking with success rates, completion percentages, and comprehensive calling statistics.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: FileText,
      title: 'PDF Reports',
      description: 'Generate professional PDF reports with detailed calling session summaries and outcome breakdowns.',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: Clock,
      title: 'Session Management',
      description: 'Pause, resume, and skip contacts during calling sessions. Never lose your progress.',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: Users,
      title: 'Contact Tracking',
      description: 'Mark outcomes as Not Answered, Asked for Callback, Not Interested, or Completed Successfully.',
      color: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'Data Security',
      description: 'All data stored locally on your device. Privacy-focused with no cloud dependency.',
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: Smartphone,
      title: 'Mobile Optimized',
      description: 'Built with Flutter for consistent experience across all devices. Works offline.',
      color: 'from-pink-500 to-pink-600'
    }
  ];

  return (
    <section id="features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Powerful Features for Professional Calling
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to manage bulk calling sessions efficiently. 
            From Excel integration to professional reporting, we've got you covered.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="group">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-primary-200 h-full">
                <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Transform Your Calling Process?
            </h3>
            <p className="text-gray-600 mb-6">
              Streamline your calling sessions with Bulk Leads Caller.
            </p>
            <button className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-3 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl">
              Download Free App
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
